/** School ordering actions: four independent Special Service folders controlled by Customers. */
import { cn } from '@ordo/ui/cn'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@ordo/ui/dropdown-menu'
import { Bus, CalendarDays, type LucideIcon, Package, Sparkles, UserRound } from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'

import { type AdminSchool } from '@/api/school-orders'
import { CHIP_CLASS, ChipButton } from '@/components/chefs/chip-button'
import { FieldTripDialog } from '@/components/orders/school/field-trip-dialog'
import { PrintCalendarDialog } from '@/components/orders/school/print-calendar-dialog'
import { StudentOrderDialog } from '@/components/orders/school/student-order-dialog'
import { SuppliesBackUpDialog } from '@/components/orders/school/supplies-back-up-dialog'
import { SuppliesTab } from '@/components/orders/school/supplies-tab'
import { DietaryNotesCard } from '@/components/settings/dietary-notes-card'
import { specialServiceOptions } from '@/lib/school-orders/special-service'

export type SchoolOrderActionsVariant = 'chips' | 'list'

export interface SchoolOrderActionsProps {
  school: AdminSchool
  /** All the admin's campuses — lets Print Calendar export the whole district. */
  schools?: AdminSchool[]
  /** `chips` — the desktop controls-row pair. `list` — the mobile Options rows. */
  variant?: SchoolOrderActionsVariant
  /** The campus can't place school meal orders; bulk purchases use their own enablement. */
  readOnly?: boolean
}

export function SchoolOrderActions({
  school,
  schools,
  variant = 'chips',
  readOnly = false,
}: SchoolOrderActionsProps): ReactElement {
  const [fieldTripOpen, setFieldTripOpen] = useState(false)
  const [suppliesBackUpOpen, setSuppliesBackUpOpen] = useState(false)
  const [specialDietsOpen, setSpecialDietsOpen] = useState(false)
  const [envelope, setEnvelope] = useState<'backup' | 'supplies'>('backup')
  const [printCalendarOpen, setPrintCalendarOpen] = useState(false)
  const [studentOrderOpen, setStudentOrderOpen] = useState(false)
  // Bulk purchases are SupplyOrders, independent of the SCHOOL meal-order method.
  const serviceSchool = useMemo(
    () =>
      readOnly
        ? { ...school, fieldTripMealsEnabled: false, specialDietsEnabled: false, backUpMealsEnabled: false }
        : school,
    [readOnly, school],
  )
  const options = useMemo(() => specialServiceOptions(serviceSchool), [serviceSchool])
  // A school's order for one student is a family-shaped order, so it follows
  // the PARENT order method — not `readOnly`, which is about SCHOOL orders. A
  // PARENT-only campus (e.g. FASPS) can still order for its students.
  const canOrderForStudent = school.parentOrderingEnabled !== false
  const hasFieldTrip = options.includes('FIELD_TRIP')
  const hasSpecialDiets = options.includes('SPECIAL_DIETS')
  const hasBackup = options.includes('BACKUP')
  const hasSupplies = options.includes('SUPPLIES')
  const hasSuppliesBackUp = hasBackup || hasSupplies
  function openEnvelope(kind: 'backup' | 'supplies') {
    setEnvelope(kind)
    setSuppliesBackUpOpen(true)
  }

  const dialogs = (
    <>
      {hasFieldTrip &&
        (school.baggedMealtimes.length > 0 ? (
          <FieldTripDialog school={school} open={fieldTripOpen} onOpenChange={setFieldTripOpen} />
        ) : (
          <Dialog open={fieldTripOpen} onOpenChange={setFieldTripOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Field Trip Meals</DialogTitle>
              </DialogHeader>
              <DialogBody>
                No field trip menu is currently available for this campus. Contact Ordo Support to set it up.
              </DialogBody>
            </DialogContent>
          </Dialog>
        ))}
      {hasSuppliesBackUp ? (
        <SuppliesBackUpDialog
          key={envelope}
          envelope={envelope}
          school={serviceSchool}
          open={suppliesBackUpOpen}
          onOpenChange={setSuppliesBackUpOpen}
          suppliesTab={
            school.bulkSuppliesEnabled && school.hasSupplyCatalog ? (
              <SuppliesTab school={school} scope={envelope} />
            ) : undefined
          }
        />
      ) : null}
      {hasSpecialDiets && (
        <Dialog open={specialDietsOpen} onOpenChange={setSpecialDietsOpen}>
          <DialogContent className="sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>Special Diets · {school.name}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <DietaryNotesCard school={school} />
            </DialogBody>
          </DialogContent>
        </Dialog>
      )}
      {canOrderForStudent ? (
        <StudentOrderDialog school={school} open={studentOrderOpen} onOpenChange={setStudentOrderOpen} />
      ) : null}
      <PrintCalendarDialog
        school={school}
        schools={schools}
        open={printCalendarOpen}
        onOpenChange={setPrintCalendarOpen}
      />
    </>
  )

  if (variant === 'list') {
    return (
      <>
        <OptionRow Icon={CalendarDays} label="Print calendar" onClick={() => setPrintCalendarOpen(true)} />
        {/* No nested menu on a phone — each special service is its own row. */}
        {canOrderForStudent ? (
          <OptionRow Icon={UserRound} label="Order for a student" onClick={() => setStudentOrderOpen(true)} />
        ) : null}
        {hasFieldTrip ? <OptionRow Icon={Bus} label="Field Trip Meals" onClick={() => setFieldTripOpen(true)} /> : null}
        {hasSpecialDiets && (
          <OptionRow Icon={Sparkles} label="Special Diets" onClick={() => setSpecialDietsOpen(true)} />
        )}
        {hasBackup && <OptionRow Icon={Package} label="Backup Meals" onClick={() => openEnvelope('backup')} />}
        {hasSupplies && <OptionRow Icon={Package} label="Supplies" onClick={() => openEnvelope('supplies')} />}
        {dialogs}
      </>
    )
  }

  // Chips keep their `lg`-only labels: the pair is desktop-only today, but the
  // collapse is what makes the row survive a narrow desktop window.
  return (
    <>
      {options.length > 0 || canOrderForStudent ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <ChipButton
              Icon={Sparkles}
              showCaret
              aria-label="Special Service"
              className={cn(CHIP_CLASS, 'max-lg:gap-0 max-lg:px-2.5')}>
              <span className="hidden lg:inline">Special Service</span>
            </ChipButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canOrderForStudent ? (
              <DropdownMenuItem onSelect={() => setStudentOrderOpen(true)}>
                <UserRound className="h-4 w-4" strokeWidth={1.75} />
                Order for a student
              </DropdownMenuItem>
            ) : null}
            {hasFieldTrip ? (
              <DropdownMenuItem onSelect={() => setFieldTripOpen(true)}>
                <Bus className="h-4 w-4" strokeWidth={1.75} />
                Field Trip Meals
              </DropdownMenuItem>
            ) : null}
            {hasSpecialDiets && (
              <DropdownMenuItem onSelect={() => setSpecialDietsOpen(true)}>
                <Sparkles className="h-4 w-4" />
                Special Diets
              </DropdownMenuItem>
            )}
            {hasBackup && (
              <DropdownMenuItem onSelect={() => openEnvelope('backup')}>
                <Package className="h-4 w-4" />
                Backup Meals
              </DropdownMenuItem>
            )}
            {hasSupplies && (
              <DropdownMenuItem onSelect={() => openEnvelope('supplies')}>
                <Package className="h-4 w-4" />
                Supplies
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      {/* Same outline chip as Special Service — the blue primary read as the
          page's main action, and variant="default" rendered borderless
          (Sheila, 2026-08-24). */}
      <ChipButton
        Icon={CalendarDays}
        aria-label="Print menu calendar"
        className={cn(CHIP_CLASS, 'max-lg:gap-0 max-lg:px-2.5')}
        onClick={() => setPrintCalendarOpen(true)}>
        <span className="hidden lg:inline">Print Calendar</span>
      </ChipButton>
      {dialogs}
    </>
  )
}

/** One row of the mobile "Options" menu: icon + label, full-width tap target. */
function OptionRow({ Icon, label, onClick }: { Icon: LucideIcon; label: string; onClick: () => void }): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-muted/50">
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
      {label}
    </button>
  )
}
